/**
 * Testimonials avec Onglets
 * - Gestion des onglets
 * - Slider Swiper sur desktop (si > 2 avis)
 * - Bouton "Voir plus" sur mobile
 */

class TestimonialsTabs {
  constructor() {
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    const tabContainers = document.querySelectorAll('.testimonials-tabs-wrapper');

    tabContainers.forEach(container => {
      this.setupTabs(container);
      this.setupSliders(container);
      this.setupShowMore(container);
    });

    // Re-init sliders on resize
    this.handleResize();
  }

  setupTabs(container) {
    const tabs = container.querySelectorAll('.testimonials-tabs__tab');
    const panels = container.querySelectorAll('.testimonials-tabs__panel');

    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        const targetPanel = tab.getAttribute('data-tab');
        
        tabs.forEach(t => {
          t.classList.remove('active', 'btn--primary');
        });
        panels.forEach(p => p.classList.remove('active'));

        tab.classList.add('active', 'btn--primary');
        const panel = container.querySelector(`[data-panel="${targetPanel}"]`);
        if (panel) {
          panel.classList.add('active');
          
          // Re-init slider for this panel if needed
          const sliderWrapper = panel.querySelector('.testimonials-tabs__slider-wrapper[data-enable-slider="true"]');
          if (sliderWrapper && sliderWrapper.swiperInstance) {
            sliderWrapper.swiperInstance.update();
          }
        }

        // Analytics tracking
        if (window.dataLayer) {
          window.dataLayer.push({
            'event': 'testimonials_tab_change',
            'tab_name': targetPanel
          });
        }
      });
    });

    // Keyboard navigation
    tabs.forEach((tab, index) => {
      tab.addEventListener('keydown', (e) => {
        let newIndex = index;
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          newIndex = (index + 1) % tabs.length;
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          newIndex = (index - 1 + tabs.length) % tabs.length;
        } else if (e.key === 'Home') {
          e.preventDefault();
          newIndex = 0;
        } else if (e.key === 'End') {
          e.preventDefault();
          newIndex = tabs.length - 1;
        }
        if (newIndex !== index) {
          tabs[newIndex].focus();
          tabs[newIndex].click();
        }
      });
    });
  }

  setupSliders(container) {
    const sliderWrappers = container.querySelectorAll('.testimonials-tabs__slider-wrapper[data-enable-slider="true"]');
    
    sliderWrappers.forEach(wrapper => {
      this.initSlider(wrapper, container);
    });
  }

  initSlider(wrapper, container) {
    // Only init slider on desktop
    if (window.innerWidth < 750) {
      this.destroySlider(wrapper);
      return;
    }

    // Already initialized
    if (wrapper.swiperInstance) return;

    const itemsContainer = wrapper.querySelector('.testimonials__items');
    const panel = wrapper.closest('.testimonials-tabs__panel');
    const controls = panel.querySelector('.testimonials-tabs__slider-controls');
    
    if (!itemsContainer || !window.FoxTheme || !window.FoxTheme.Carousel) {
      return;
    }

    // Get column gap
    const columnGap = FoxTheme.utils ? FoxTheme.utils.getGridColumnGap(itemsContainer) : { desktop: 24, tablet: 20, mobile: 16 };

    // Add swiper classes
    wrapper.classList.add('swiper');
    itemsContainer.classList.remove('f-grid');
    itemsContainer.classList.add('swiper-wrapper');
    
    // Add swiper-slide class to items
    const items = itemsContainer.querySelectorAll('.f-column');
    items.forEach(item => {
      item.classList.add('swiper-slide');
    });

    const sliderOptions = {
      slidesPerView: 2,
      spaceBetween: columnGap.desktop || 24,
      navigation: {
        nextEl: controls ? controls.querySelector('.swiper-btn-next') : null,
        prevEl: controls ? controls.querySelector('.swiper-btn-prev') : null,
      },
      pagination: false,
      loop: items.length > 2,
      threshold: 2,
      breakpoints: {
        750: {
          slidesPerView: 2,
          spaceBetween: columnGap.tablet || 20
        },
        1024: {
          slidesPerView: 2,
          spaceBetween: columnGap.desktop || 24
        }
      }
    };

    wrapper.swiperInstance = new window.FoxTheme.Carousel(wrapper, sliderOptions);
    wrapper.swiperInstance.init();
  }

  destroySlider(wrapper) {
    if (!wrapper.swiperInstance) return;

    const itemsContainer = wrapper.querySelector('.testimonials__items');
    
    wrapper.classList.remove('swiper');
    if (itemsContainer) {
      itemsContainer.classList.add('f-grid');
      itemsContainer.classList.remove('swiper-wrapper');
    }
    
    const items = wrapper.querySelectorAll('.f-column');
    items.forEach(item => {
      item.classList.remove('swiper-slide');
    });

    if (wrapper.swiperInstance.slider) {
      wrapper.swiperInstance.slider.destroy();
    }
    wrapper.swiperInstance = null;
  }

  setupShowMore(container) {
    const showMoreBtns = container.querySelectorAll('.show-more-btn');
    
    showMoreBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const panelName = btn.getAttribute('data-panel');
        const panel = container.querySelector(`[data-panel="${panelName}"]`);
        
        if (!panel) return;

        // Show all hidden items with staggered animation
        const hiddenItems = panel.querySelectorAll('.testimonial-item.mobile-hidden');
        hiddenItems.forEach((item, index) => {
          setTimeout(() => {
            item.classList.add('show');
          }, index * 100); // Stagger animation by 100ms
        });

        // Hide the button
        const showMoreContainer = btn.closest('.testimonials-tabs__show-more');
        if (showMoreContainer) {
          showMoreContainer.classList.add('hidden');
        }
      });
    });
  }

  handleResize() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const containers = document.querySelectorAll('.testimonials-tabs-wrapper');
        containers.forEach(container => {
          const sliderWrappers = container.querySelectorAll('.testimonials-tabs__slider-wrapper[data-enable-slider="true"]');
          sliderWrappers.forEach(wrapper => {
            if (window.innerWidth < 750) {
              this.destroySlider(wrapper);
            } else {
              this.initSlider(wrapper, container);
            }
          });
        });
      }, 250);
    });
  }
}

// Initialize
new TestimonialsTabs();
